import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import '../Education.css';

const ForexEducation: React.FC = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
    }
  };

  const curriculum = [
    {
      title: 'Forex Fundamentals',
      lessons: [
        'What is Forex and how does the market work',
        'Major, minor, and exotic currency pairs',
        'Understanding pips, lots, and leverage',
        'How to read currency quotes',
        'Market participants and their influence',
        'Forex trading sessions and best times to trade'
      ]
    },
    {
      title: 'Technical Analysis for Forex',
      lessons: [
        'Candlestick patterns specific to forex',
        'Support and resistance in currency trading',
        'Trend analysis and trendlines',
        'Moving averages for forex pairs',
        'Fibonacci retracements in FX markets',
        'Chart patterns: Head & Shoulders, Double Tops/Bottoms'
      ]
    },
    {
      title: 'Fundamental Analysis',
      lessons: [
        'Economic indicators and their impact (GDP, NFP, CPI)',
        'Central bank policies and interest rates',
        'How news events move currency prices',
        'Understanding the economic calendar',
        'Correlation between currencies and commodities',
        'Geopolitical events and forex markets'
      ]
    },
    {
      title: 'Forex Trading Strategies',
      lessons: [
        'Scalping strategies for quick profits',
        'Day trading forex pairs',
        'Swing trading multi-day positions',
        'Position trading for long-term trends',
        'Carry trade strategies',
        'Breakout and reversal strategies'
      ]
    },
    {
      title: 'Risk Management in Forex',
      lessons: [
        'Position sizing for forex trades',
        'Setting stop-loss and take-profit levels',
        'Understanding leverage and margin',
        'Managing correlation between pairs',
        'Risk-to-reward ratios in currency trading',
        'Common forex trading mistakes to avoid'
      ]
    }
  ];

  const tradingPairs = [
    { pair: 'EUR/USD', nickname: 'Euro', description: 'Most traded currency pair, high liquidity' },
    { pair: 'GBP/USD', nickname: 'Cable', description: 'Volatile pair, popular among traders' },
    { pair: 'USD/JPY', nickname: 'Gopher', description: 'Safe-haven pair, low spreads' },
    { pair: 'USD/CHF', nickname: 'Swissy', description: 'Another safe-haven currency' },
    { pair: 'AUD/USD', nickname: 'Aussie', description: 'Commodity-linked currency' },
    { pair: 'USD/CAD', nickname: 'Loonie', description: 'Correlated with oil prices' }
  ];

  return (
    <motion.div 
      className="education-page"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <div className="page-content">
        <motion.div className="page-title" variants={itemVariants}>
          <h1>Forex Trading Education</h1>
          <p>Master currency trading with comprehensive forex education</p>
        </motion.div>

        <motion.section className="course-overview" variants={itemVariants}>
          <h2>Why Learn Forex Trading?</h2>
          <p>
            The foreign exchange (Forex) market is the largest and most liquid financial market in the world, 
            with over $7.5 trillion traded daily. Learning to trade forex opens up opportunities to profit from 
            global economic events, trade 24 hours a day, and access the most dynamic market available to retail traders.
          </p>
          <div className="course-stats">
            <div className="stat">
              <h3>24/5 Trading</h3>
              <p>Market Never Sleeps</p>
            </div>
            <div className="stat">
              <h3>$7.5 Trillion</h3>
              <p>Daily Volume</p>
            </div>
            <div className="stat">
              <h3>High Leverage</h3>
              <p>Up to 1:500</p>
            </div>
            <div className="stat">
              <h3>Low Costs</h3>
              <p>Tight Spreads</p>
            </div>
          </div>
        </motion.section>

        <motion.section className="learning-path" variants={itemVariants}>
          <h2>Complete Forex Curriculum</h2>
          <div className="modules-list">
            {curriculum.map((module, index) => (
              <motion.div 
                key={index} 
                className="module-card"
                variants={itemVariants}
                whileHover={{ scale: 1.02 }}
              >
                <div className="module-header">
                  <span className="module-number">Module {index + 1}</span>
                </div>
                <h3>{module.title}</h3>
                <ul className="topics-list">
                  {module.lessons.map((lesson, lessonIndex) => (
                    <li key={lessonIndex}>{lesson}</li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </motion.section>

        <motion.section className="major-pairs-section" variants={itemVariants}>
          <h2>Major Currency Pairs You'll Trade</h2>
          <div className="pairs-grid">
            {tradingPairs.map((item, index) => (
              <motion.div 
                key={index} 
                className="pair-card"
                variants={itemVariants}
                whileHover={{ scale: 1.05 }}
              >
                <h3 style={{color: 'white'}}>{item.pair}</h3>
                <p className="nickname">"{item.nickname}"</p>
                <p>{item.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        <motion.section className="trading-sessions" variants={itemVariants}>
          <h2>Forex Trading Sessions</h2>
          <div className="sessions-grid">
            <div className="session">
              <h3>🇦🇺 Sydney Session</h3>
              <p><strong>Time:</strong> 10:00 PM - 7:00 AM GMT</p>
              <p>Lower volatility, ideal for AUD and NZD pairs</p>
            </div>
            <div className="session">
              <h3>🇯🇵 Tokyo Session</h3>
              <p><strong>Time:</strong> 12:00 AM - 9:00 AM GMT</p>
              <p>Asian market hours, best for JPY pairs</p>
            </div>
            <div className="session">
              <h3>🇬🇧 London Session</h3>
              <p><strong>Time:</strong> 8:00 AM - 5:00 PM GMT</p>
              <p>Highest volume and volatility, EUR and GBP active</p>
            </div>
            <div className="session">
              <h3>🇺🇸 New York Session</h3>
              <p><strong>Time:</strong> 1:00 PM - 10:00 PM GMT</p>
              <p>Overlaps with London, maximum liquidity for USD pairs</p>
            </div>
          </div>
        </motion.section>

        <motion.section className="key-concepts" variants={itemVariants}>
          <h2>Essential Forex Concepts</h2>
          <div className="concepts-grid">
            <div className="concept">
              <h4 style={{color: '#06b6d4'}}>📊 Pip Value</h4>
              <p>The smallest price movement in a currency pair, typically 0.0001</p>
            </div>
            <div className="concept">
              <h4 style={{color: '#06b6d4'}}>💰 Lot Sizes</h4>
              <p>Standard (100k), Mini (10k), Micro (1k) lot sizes for position sizing</p>
            </div>
            <div className="concept">
              <h4 style={{color: '#06b6d4'}}>⚖️ Leverage</h4>
              <p>Borrow capital to control larger positions (use with caution)</p>
            </div>
            <div className="concept">
              <h4 style={{color: '#06b6d4'}}>📈 Spread</h4>
              <p>Difference between bid and ask price, your trading cost</p>
            </div>
            <div className="concept">
              <h4 style={{color: '#06b6d4'}}>🔄 Rollover/Swap</h4>
              <p>Interest earned or paid for holding positions overnight</p>
            </div>
            <div className="concept">
              <h4 style={{color: '#06b6d4'}}>📉 Margin</h4>
              <p>Amount of capital required to open a leveraged position</p>
            </div>
          </div>
        </motion.section>

        <motion.section className="practical-exercises" variants={itemVariants}>
          <h2>Hands-On Learning</h2>
          <div className="exercises-list">
            <div className="exercise">
              <h4 style={{color: '#06b6d4'}}>✓ Demo Trading Practice</h4>
              <p>Practice on a demo account with $10,000 virtual capital before risking real money</p>
            </div>
            <div className="exercise">
              <h4 style={{color: '#06b6d4'}}>✓ Live Chart Analysis</h4>
              <p>Analyze live forex charts and identify trading opportunities in real-time</p>
            </div>
            <div className="exercise">
              <h4 style={{color: '#06b6d4'}}>✓ Economic Calendar Tracking</h4>
              <p>Learn to track and trade major economic news releases</p>
            </div>
            <div className="exercise">
              <h4 style={{color: '#06b6d4'}}>✓ Trading Journal</h4>
              <p>Maintain a detailed trading journal to track your learning progress</p>
            </div>
            <div className="exercise">
              <h4 style={{color: '#06b6d4'}}>✓ Strategy Backtesting</h4>
              <p>Test trading strategies using historical data before going live</p>
            </div>
          </div>
        </motion.section>

        <motion.section className="success-tips" variants={itemVariants}>
          <h2>Forex Trading Success Tips</h2>
          <ul className="tips-list">
            <li>Start with major pairs (EUR/USD, GBP/USD) - they have the tightest spreads</li>
            <li>Trade during peak liquidity hours (London-New York overlap)</li>
            <li>Never risk more than 1-2% of your account on a single trade</li>
            <li>Always use stop-loss orders to limit potential losses</li>
            <li>Master one or two pairs before expanding to others</li>
            <li>Keep up with economic calendars and central bank announcements</li>
            <li>Avoid trading during major news events until you're experienced</li>
            <li>Practice on demo for at least 3-6 months before going live</li>
          </ul>
        </motion.section>

        <motion.div className="cta-section" variants={itemVariants}>
          <h2>Start Your Forex Trading Journey</h2>
          <p>Get professional forex education and trading signals</p>
          <div className="btn-container">
            <Link to="/contact" className="btn">Enroll in Forex Course</Link>
            <Link to="/services/signals" className="btn secondary">Get Forex Signals</Link>
            <Link to="/markets/forex" className="btn secondary">Learn About Forex Market</Link>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default ForexEducation;
